import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cachedPatientService, cachedInsuranceService } from '../services/cachedApiService';
import { Patient, Insurance } from '../types';
import Layout from '../components/Layout';
import '../styles/PatientList.css';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';

const PatientList: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        
        let patientData: Patient[] = [];
        
        // If user is a provider, fetch only assigned patients
        if (user?.role === 'provider') {
          const assignedPids = await cachedPatientService.getAssignedPatients(user.username);
          const allPatients = await cachedPatientService.getAllPatients();
          patientData = allPatients.filter(patient => assignedPids.includes(patient.pid));
        } else if (user?.role === 'superuser') {
          // If user is a superuser, fetch all patients
          patientData = await cachedPatientService.getAllPatients();
        }
        
        // Fetch all insurance data to check for ePA
        const insurances = await cachedInsuranceService.getAllInsurance();
        
        // Add ePA flag to each patient
        const patientsWithEPA = patientData.map(patient => {
          const hasEPA = insurances.some(
            ins => ins.patient === patient.pid && ins.ePA
          );
          return { ...patient, has_ePA: hasEPA };
        });
        
        setPatients(patientsWithEPA);
        setFilteredPatients(patientsWithEPA);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch patients');
        setLoading(false);
      }
    };

    fetchPatients();
  }, [user]);

  const handleDeletePatient = async (patientId: string, patientName: string) => {
    if (!window.confirm(`Are you sure you want to delete patient ${patientName} (${patientId})? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setError(null);
      setSuccessMessage(null);
      await cachedPatientService.deletePatient(patientId);
      
      // Invalidate related caches
      invalidate.patients(patientId);
      invalidate.priorAuths();
      
      // Remove the patient from the list
      const updatedPatients = patients.filter(p => p.pid !== patientId);
      setPatients(updatedPatients);
      setFilteredPatients(updatedPatients.filter(patient =>
        patient.fname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.lname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.pid.toLowerCase().includes(searchTerm.toLowerCase())
      ));
      
      // Show success message
      setSuccessMessage(`Patient ${patientName} has been deleted successfully.`);
    } catch (err) {
      setError('Failed to delete patient. Please try again.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading patients...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error">
          {error}
          <button
            onClick={() => {
              invalidate.patients();
              invalidate.insurance();
              window.location.reload();
            }}
            className="btn btn-primary mt-3"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <h2 className="mb-4 d-flex align-items-center justify-content-center gap-2">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Patient List
        </h2>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search patients by name "
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.trim() === '') {
                  setFilteredPatients(patients);
                } else {
                  setFilteredPatients(
                    patients.filter(patient =>
                      patient.fname.toLowerCase().includes(value.toLowerCase()) ||
                      patient.lname.toLowerCase().includes(value.toLowerCase()) ||
                      patient.pid.toLowerCase().includes(value.toLowerCase())
                    )
                  );
                }
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredPatients(patients);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="search-results-count">
            {filteredPatients.length} {filteredPatients.length === 1 ? 'patient' : 'patients'} found
          </div>
        </div>
        
        {successMessage && (
          <div className="alert alert-success mb-4 fade-in">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger mb-4 fade-in">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {error}
          </div>
        )}

        <ul className="p-0" style={{ listStyle: 'none' }}>
          {filteredPatients.length > 0 ? (
            filteredPatients.map((patient) => (
              <li key={patient.pid} className="patient-card fade-in">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar">
                    {patient.fname.charAt(0)}{patient.lname.charAt(0)}
                  </div>
                  <div className="patient-name">
                    <Link to={`/patients/${patient.pid}`}>
                      {patient.fname} {patient.lname} <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem', fontWeight: 'normal' }}></span>
                    </Link>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Link to={`/prior-authorizations/${patient.pid}`}>
                    <button className="btn btn-primary btn-sm">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                        <path d="M9 12H15M9 16H15M9 8H15M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      PA
                    </button>
                  </Link>

                  {patient.has_ePA ? (
                    <span className="status-badge success">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                        <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      ePA
                    </span>
                  ) : (
                    <Link to={`/raise-pa/${patient.pid}`}>
                      <button className="btn btn-success btn-sm">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        New PA
                      </button>
                    </Link>
                  )}
                  
                  {/* Delete button - only visible to admin users */}
                  {user?.role === 'superuser' && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeletePatient(patient.pid, `${patient.fname} ${patient.lname}`)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                        <path d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Del
                    </button>
                  )}
                </div>
              </li>
            ))
          ) : (
            <li className="empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px', opacity: 0.5 }}>
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <div>
                {patients.length > 0
                  ? `No patients found matching "${searchTerm}"`
                  : "No patients found."}
              </div>
              {patients.length > 0 && searchTerm && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '10px' }}
                  onClick={() => {
                    setSearchTerm('');
                    setFilteredPatients(patients);
                  }}
                >
                  Clear search
                </button>
              )}
            </li>
          )}
        </ul>

      </div>
    </Layout>
  );
};

export default PatientList;